import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eyebrow, Headline } from "@/components/Editorial";
import { Spinner } from "@/components/Loader";
import { affGet } from "@/lib/affiliateApi";
import { getLocalAcknowledgment, clearLocalAcknowledgment, needsAcknowledgment } from "@/components/affiliate/ComplianceGate";
import { toast } from "@/components/Toast";
import { C } from "@/lib/brand";

// Program playbook index. Each playbook is a long-form markdown doc
// with audience-fit, talking points, and compliance notes per program.
//
// API contract:
//   GET /affiliate/content/playbooks →
//     { playbooks: [{slug, title, programName, summary, lastUpdatedAt,
//                    acknowledgedAt?, requiresAcknowledgment?}] }
export default function AssetsPlaybooks() {
  const [list, setList] = useState(null);
  const [bumpKey, setBumpKey] = useState(0); // re-read CTA re-renders ack chip
  const navigate = useNavigate();
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const norm = (raw) =>
        raw
          .map((p) => {
            const programName =
              p.name || p.programName || p.title || p.label || p.programSlug || p.slug || "Program";
            const slug = p.slug || p.programSlug || p.id;
            return {
              slug,
              title: p.playbookTitle || `${programName} Playbook`,
              programName,
              summary: p.summary || p.subtitle || "",
              lastUpdatedAt: p.lastUpdatedAt || p.lastEditedByAdminAt || p.publishedAt,
              // BE may or may not return acknowledgedAt — when absent we
              // fall back to the local mirror written by ComplianceGate.
              acknowledgedAt: p.acknowledgedAt || p.metadata?.acknowledgedAt || null,
              requiresAcknowledgment:
                p.requiresAcknowledgment === true ||
                p.metadata?.requiresAcknowledgment === true ||
                !!p.complianceWarning,
            };
          })
          .filter((p) => p.slug);

      // Primary: query the universal content endpoint with the BE's
      // canonical contentType enum. The admin's filter chip is labeled
      // "Playbook" but the underlying enum value is `playbook_section`
      // — confirmed against the BE's allow-list error response.
      try {
        const res = await affGet(
          "/affiliate/content?contentType=playbook_section",
          { auth: true },
        );
        const items = res?.items || res?.content || (Array.isArray(res) ? res : []);
        if (items.length > 0) {
          if (!cancelled) {
            setList(norm(items.map((it) => ({
              // Prefer programSlug so the reader URL routes correctly
              // (the singular reader endpoint resolves the published
              // playbook for that program).
              slug: it.programSlug || it.slug || it.id,
              programName: it.programName || it.programSlug,
              playbookTitle: it.title,
              summary: it.summary || it.subtitle,
              lastUpdatedAt: it.lastEditedByAdminAt || it.publishedAt,
              requiresAcknowledgment: it.requiresAcknowledgment === true || !!it.complianceWarning,
              acknowledgedAt: it.acknowledgedAt,
            }))));
          }
          return;
        }
      } catch (err) {
        if (err?.status && err.status !== 404) {
          // eslint-disable-next-line no-console
          console.warn("[playbooks] /content?contentType=playbook_section failed", err);
        }
      }

      // Secondary: /content/programs — the BE's "what programs have
      // playbooks" list. Filter out programs explicitly marked as not
      // having a playbook.
      try {
        const res = await affGet("/affiliate/content/programs", { auth: true });
        const raw = (res?.programs || res?.items || []).filter(
          (p) => p && p.hasPlaybook !== false,
        );
        if (raw.length > 0) {
          if (!cancelled) setList(norm(raw));
          return;
        }
      } catch (err) {
        if (err?.status && err.status !== 404) {
          // eslint-disable-next-line no-console
          console.warn("[playbooks] /content/programs failed", err);
        }
      }

      // All three discovery paths returned empty/404. As a last-mile
      // fallback, probe the singular `/playbook/{slug}` endpoint for
      // the known program slugs. This is what the playbook reader
      // already uses, so any published playbook will surface here even
      // if the BE hasn't wired up a list endpoint yet. We do this in
      // parallel to keep page-load fast (settle in one round-trip).
      const KNOWN_SLUGS = [
        "weight-management",
        "recovery-healing",
        "skin-beauty",
        "longevity",
        "cognitive-performance",
        "energy-vitality",
        "sexual-wellness",
      ];
      try {
        const results = await Promise.all(
          KNOWN_SLUGS.map(async (slug) => {
            try {
              const doc = await affGet(`/affiliate/content/playbook/${slug}`, { auth: true });
              if (!doc) return null;
              // BE wraps the row in `.item` on some shapes, returns it
              // bare on others. Normalize.
              const piece = doc.item || doc;
              if (!piece || (!piece.body && !piece.body_md && !piece.title)) return null;
              return {
                slug,
                programName: piece.programName || piece.metadata?.programName || slug,
                playbookTitle: piece.title,
                summary: piece.summary || piece.subtitle,
                lastUpdatedAt: piece.lastEditedByAdminAt || piece.publishedAt || piece.lastUpdatedAt,
                requiresAcknowledgment:
                  piece.requiresAcknowledgment === true ||
                  piece.metadata?.requiresAcknowledgment === true ||
                  !!piece.complianceWarning,
                acknowledgedAt: piece.acknowledgedAt || piece.metadata?.acknowledgedAt,
              };
            } catch {
              return null;
            }
          }),
        );
        const found = results.filter(Boolean);
        if (found.length > 0) {
          if (!cancelled) setList(norm(found));
          return;
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("[playbooks] slug probe failed", err);
      }

      // All discovery paths exhausted — render the "preparing" empty state.
      if (!cancelled) setList([]);
    })();
    return () => { cancelled = true; };
  }, []);

  const handleReread = (slug, e) => {
    e.preventDefault();
    e.stopPropagation();
    clearLocalAcknowledgment(slug);
    setBumpKey((k) => k + 1);
    toast("Acknowledgment cleared — we'll show the read-first guide again.", "info");
    navigate(`/affiliates/dashboard/assets/playbooks/${slug}`);
  };

  if (list === null) {
    return <div style={{ padding: 64, textAlign: "center" }}><Spinner size={28} /></div>;
  }

  return (
    <div style={{ maxWidth: 1100, margin: "0 auto", padding: "40px 24px 64px" }}>
      <Eyebrow color={C.peachDeep}>★ PROGRAM PLAYBOOKS</Eyebrow>
      <Headline before="DEEP DIVES" italic="by program." size={48} className="mt-4" />
      <p style={{ marginTop: 16, fontSize: 15, color: C.warmGray, maxWidth: 640, lineHeight: 1.6 }}>
        Each playbook explains how to think about a single program — who it's for, what to highlight, where to be careful.
      </p>
      {list.length === 0 ? (
        <div data-testid="playbooks-empty" style={{ marginTop: 28, padding: 24, background: C.bone, fontSize: 14, color: C.warmGray, fontFamily: '"Instrument Serif", Georgia, serif', fontStyle: "italic" }}>
          Playbooks are being prepared — check back soon.
        </div>
      ) : (
        <ul
          data-testid="playbook-list"
          style={{
            listStyle: "none",
            margin: "32px 0 0",
            padding: 0,
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
            gap: 18,
          }}
        >
          {list.map((p) => {
            const ackBe = p.acknowledgedAt ? new Date(p.acknowledgedAt) : null;
            const ackLocal = getLocalAcknowledgment(p.slug); // re-evaluated each render
            const ackAt = ackBe || ackLocal;
            const showsAckUI = !!ackAt && (p.requiresAcknowledgment || needsAcknowledgment({ requiresAcknowledgment: p.requiresAcknowledgment }, p.slug));
            return (
              <li key={`${p.slug}-${bumpKey}`}>
                <Link
                  to={`/affiliates/dashboard/assets/playbooks/${p.slug}`}
                  data-testid={`playbook-card-${p.slug}`}
                  style={{
                    textDecoration: "none",
                    display: "block",
                    background: C.pure,
                    border: `1px solid ${C.warmGray3}`,
                    padding: 22,
                    color: C.ink,
                    transition: "transform 140ms",
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; }}
                >
                  <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.22em", color: C.peachDeep }}>
                    ★ {(p.programName || "PROGRAM").toUpperCase()}
                  </div>
                  <div style={{ marginTop: 6, fontSize: 22, fontWeight: 800, lineHeight: 1.15, letterSpacing: "-0.01em" }}>
                    {p.title}
                  </div>
                  {p.summary && (
                    <p style={{ marginTop: 10, fontSize: 13, lineHeight: 1.6, color: C.warmGray }}>
                      {p.summary}
                    </p>
                  )}

                  {showsAckUI && (
                    <div
                      data-testid={`playbook-ack-${p.slug}`}
                      style={{
                        marginTop: 14,
                        padding: "10px 12px",
                        background: C.bone,
                        borderLeft: `2px solid ${C.sageDeep}`,
                        fontSize: 12,
                        color: C.warmGray,
                        lineHeight: 1.5,
                      }}
                    >
                      <span style={{ color: C.sageDeep, fontWeight: 800, letterSpacing: "0.08em", marginRight: 6 }}>
                        ✓ ACKNOWLEDGED
                      </span>
                      <span style={{ color: C.ink }}>
                        {fmtAckDate(ackAt)}
                      </span>
                    </div>
                  )}

                  <div style={{ marginTop: 14, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.22em", color: C.ink }}>
                      READ →
                    </span>
                    {showsAckUI && (
                      <button
                        type="button"
                        onClick={(e) => handleReread(p.slug, e)}
                        data-testid={`playbook-reread-${p.slug}`}
                        style={{
                          background: "transparent",
                          border: 0,
                          padding: 0,
                          fontSize: 10,
                          fontWeight: 800,
                          letterSpacing: "0.2em",
                          color: C.peachDeep,
                          textDecoration: "underline",
                          cursor: "pointer",
                        }}
                      >
                        RE-READ PLAYBOOK
                      </button>
                    )}
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

// "Jan 14, 2026" — concise, audit-friendly. Same locale style used
// on the protocol card so dates feel consistent across surfaces.
function fmtAckDate(d) {
  if (!d) return "";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

